// Library Methods
import { useTranslation } from "react-i18next";
import { useCallback } from "react";

// Components
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import { SelectChangeEvent } from "@mui/material/Select";
import { useTheme } from "@mui/material/styles";
import SelectDropdown from "src/components/shared/inputs/SelectDropdown/SelectDropdown";
import { CaretDownIcon } from "src/assets/icons";

// Styles
import {
  ModalCustomCancel,
  ModalCustomConfirmGrey,
  getSurfaceTokens,
} from "src/styles/mui/muiStylesHelper";

// Contexts
import {
  SortByOption,
  SortDirection,
} from "src/contexts/exams/ExternalExamsContext";

type SortHeaderType = {
  smallerThanMd: boolean;
  sortOptions: Array<{ id: SortByOption; label: string }>;
  effectiveSortBy: SortByOption | null;
  sortDirection: SortDirection;
  onSortByClick: (option: SortByOption) => void;
  selectedCount: number;
};

const SortHeader = ({
  smallerThanMd,
  sortOptions,
  effectiveSortBy,
  sortDirection,
  onSortByClick,
  selectedCount,
}: SortHeaderType) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { surface, onSurface } = getSurfaceTokens(theme);

  // SelectDropdown matches its MenuItems by `displayName`, not `value` --
  // so its own `value` prop and each option's `value` field both need to be
  // the label, and we map back to the internal SortByOption id ourselves.
  const sortDropdownOptions = sortOptions.map((option) => ({
    displayName: option.label,
    value: option.label,
  }));

  const sortDropdownValue =
    sortOptions.find((option) => option.id === effectiveSortBy)?.label ?? "";

  const handleSortDropdownChange = useCallback(
    (event: SelectChangeEvent<any>) => {
      const selectedOption = sortOptions.find(
        (option) => option.label === event.target.value
      );
      if (selectedOption) onSortByClick(selectedOption.id as SortByOption);
    },
    [sortOptions, onSortByClick]
  );

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: "8px",
        height: "53px",
        minHeight: "53px",
        width: "100%",
        borderBottom:
          theme.palette.mode === "dark"
            ? "1px solid #5B6D7A"
            : "1px solid #5B6D7A50",
        px: "16px",
        position: "sticky",
        top: 0,
        zIndex: 10,
        backgroundColor: surface,
        color: onSurface,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
        }}
      >
        <Typography fontSize="12px">{t("Sort by")}:</Typography>

        {smallerThanMd ? (
          <Box sx={{ width: "150px" }}>
            <SelectDropdown
              id="sort-dropdown"
              dropdownOptions={sortDropdownOptions}
              value={sortDropdownValue}
              handleChange={handleSortDropdownChange}
            />
          </Box>
        ) : (
          sortOptions.map((option) => (
            <Button
              key={option.id}
              disableRipple
              onClick={() => onSortByClick(option.id)}
              endIcon={
                effectiveSortBy === option.id ? (
                  <CaretDownIcon
                    style={{
                      width: "14px",
                      height: "14px",
                      transform:
                        sortDirection === "asc"
                          ? "rotate(180deg)"
                          : "rotate(0deg)",
                    }}
                  />
                ) : undefined
              }
              sx={
                effectiveSortBy === option.id
                  ? ModalCustomConfirmGrey
                  : ModalCustomCancel
              }
            >
              {option.label}
            </Button>
          ))
        )}
      </Box>

      <Typography fontSize="12px" fontWeight={600}>
        {selectedCount} {t("selected", { count: selectedCount })}
      </Typography>
    </Box>
  );
};

export default SortHeader;
